import { Injectable, OnInit } from "@angular/core";
import { HttpHeaders, HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { State } from "../reducers";

import {
  loginSucess,
  login,
  logout,
  logoutSucess,
  requestPermissions,
  genericError,
  setNextcloudJwt,
} from "../actions/news.actions";
import { Observable } from "rxjs";
import { CookieService } from "./cookie.service";

const SERVER_URL = environment.SERVER_URL;

@Injectable({
  providedIn: "root",
})
export class AuthService {
  username = null;
  role;
  terms;
  settings = {
    check: false,
    confirmed: false,
  };

  permissions;
  permissions$: Observable<any> = this.store.select(
    (state) => state.permissions
  );

  constructor(
    private http: HttpClient,
    private router: Router,
    private store: Store<State>,
    private cookieService: CookieService
  ) {
    this.settings.check = localStorage.getItem("check") === "true";
    this.settings.confirmed = localStorage.getItem("confirmed") === "true";
    this.permissions$.subscribe((e) => {
      this.permissions = e;
    });
  }

  public getPermissions(): string[] {
    try {
      // Check if permissions exist
      if (!this.permissions) {
        console.warn('No permissions found in service');
        return [];
      }

      // Convert object to array if necessary
      if (typeof this.permissions === 'object' && !Array.isArray(this.permissions)) {
        const permissionArray = Object.entries(this.permissions)
          .filter(([_, value]) => value === true)
          .map(([key]) => key);
        //console.log('Converted permissions to array:', permissionArray);
        return permissionArray;
      }

      // Return array if it's already an array
      if (Array.isArray(this.permissions)) {
        return this.permissions;
      }

      console.warn('Invalid permissions format:', this.permissions);
      return [];
    } catch (error) {
      console.error('Error getting permissions:', error);
      return [];
    }
  }
  public async refreshPermissions(): Promise<void> {
    try {
      //console.log('Refreshing permissions...');
      const response: any = await this.http.get(`${environment.SERVER_URL}/auth/me`).toPromise();

      if (response && response.permissions) {
        this.permissions = response.permissions;
        //console.log('Permissions refreshed:', this.permissions);
      }
    } catch (error) {
      console.error('Error refreshing permissions:', error);
    }
  }
  public getUsername() {
    return this.username;
  }
  public getRole() {
    return this.role;
  }

  public async isAuthenticated(): Promise<boolean> {
    if (this.username) return true;
    else {
      return new Promise((res) => {
        this.http
          .get(`${environment.SERVER_URL}/oauth/checkAuth`)
          .toPromise()
          .then((e: any) => {
            this.username = e.username;
            this.role = e.role;
            this.store.dispatch(requestPermissions({ role: e.role }));
            this.store.dispatch(
              loginSucess({ role: e.role, username: e.username })
            );
            res(true);
          })
          .catch((err) => {
            console.log("failed");
            res(false);
          });
      });
    }
  }
  public setCheck(check) {
    this.settings.check = check;
    localStorage.setItem("check", check);
  }
  public setConfirmed(confirmed) {
    this.settings.confirmed = confirmed;
    localStorage.setItem("confirmed", confirmed);
  }

  public authenticate(code) {
    const httpOptions: any = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      observe: "response",
    }; // TODO move to login component
    return this.http
      .post(`${SERVER_URL}/oauth/getJwtToken`, { code }, httpOptions)
      .subscribe({
        next: (data: any) => {
          const body = data.body;
          this.cookieService.setCookie("jwt", data.body.token);
          this.cookieService.deleteCookie("state");
          this.store.dispatch(requestPermissions({ role: body.role }));
          this.store.dispatch(
            loginSucess({ role: body.role, username: body.username })
          );
          setNextcloudJwt(data.body.token);
          this.username = body.username;
          this.role = body.role;
          if (body.role !== "restricted") {
            this.router.navigate(["/news"]);
          } else {
            this.router.navigate(["/permissionError"]);
          }
        },
        error: () => {
          this.store.dispatch(
            genericError({
              error: "NextCloud-Error: Please try again!",
            })
          );
        },
      });
  }
  public checkJwt() {
    return this.http.get(`${SERVER_URL}/oauth/checkJwtToken`);
  }
  public logout() {
    this.cookieService.deleteCookie("jwt");
    this.store.dispatch(logout());
    this.store.dispatch(logoutSucess());
    this.http
      .get(`${SERVER_URL}/logout`, { responseType: "text" })
      .toPromise()
      .then(() => {
        console.log("Logout successful");
      })
      .catch((e) => {
        console.log("Error on logout: ", e);
      });
  }

  public async updateUser(
    username,
    role,
    description,
    email,
    firstname,
    lastname,
    title,
    tel,
    skills,
    organization,
    id,
    terms
  ) {
    const httpOptions: any = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
    let result = null;
    try {
      result = await this.http
        .put(
          `${SERVER_URL}/user/${id}`,
          {
            username,
            role,
            hpsGroups: terms,
            description,
            email,
            firstname,
            lastname,
            title,
            tel,
            skills,
            organization,
          },
          httpOptions
        )
        .toPromise();
      // this.notificationService.displaySnackbar("User updated!");
      // TODO
    } catch (err) {
      this.logout();
      this.router.navigate(["login"]);
      // this.notificationService.displaySnackbar("Please login with your new username!");
      // TODO
      throw new Error("Please login again");
    }

    return result;
  }

  public deleteUser(id) {
    return this.http.delete(`${SERVER_URL}/user/${id}`).toPromise();
  }

  public getUser(id) {
    return this.http.get(`${SERVER_URL}/user/${id}`).toPromise();
  }
  public getUsers() {
    return this.http.get(`${SERVER_URL}/user`).toPromise();
  }

  public getUsersBrief() {
    return this.http.get(`${SERVER_URL}/user?brief`).toPromise();
  }

  public getNextcloudData(): Observable<any> {
    return this.http.get(`${SERVER_URL}/oauth/getNcData`);
  }
}